import { FC, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRectangleList } from "@fortawesome/pro-regular-svg-icons";
import { format } from "date-fns";
import { ExportModal } from "./ExportModal";
import { SegmentDialog } from "@/components/Segment/SegmentDialog";
import { useWorkspaceStore } from "@/store/workspace/WorkspaceStore";
import { useCombinedFilters } from "@/hooks/useCombinedFilters";
import {
  useExportDisabledMessage,
  useExportVisibility,
} from "@/hooks/useExportVisibility";
import { transformWorkspaceToCompanyFilter } from "@/utils/workspace/transformWorkspaceToCompanyFilter";
import { Market, ViewOption } from "@/types/workspace";

export const ExportMenuButton: FC = () => {
  const workspace = useWorkspaceStore((s) => s.draft);
  const userFilters = useWorkspaceStore((s) => s.userFilters);
  const selectedView = useWorkspaceStore((s) => s.selectedView);
  const combinedFilters = useCombinedFilters();
  const canExport = useExportVisibility();
  const exportDisabledMessage = useExportDisabledMessage();

  const [isSegmentDialogOpen, setIsSegmentDialogOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const defaultSegmentName = useMemo(() => {
    if (!workspace?.name) return "";
    return `${workspace.name} — ${format(new Date(), "yyyy-MM-dd")}`;
  }, [workspace?.name]);

  const segmentFiltersJson = useMemo(() => {
    const compiled = transformWorkspaceToCompanyFilter(
      Market.SOM,
      workspace,
      userFilters
    );
    return JSON.stringify(compiled ?? {});
  }, [workspace, userFilters]);

  if (!workspace) return null;

  const isFiltersApplied = Object.keys(combinedFilters || {}).length > 0;
  const isCompanyListTab = selectedView === ViewOption.COMPANY_LIST;
  const hasSegmentFilters = segmentFiltersJson !== "{}";

  const isExportDisabled = !isFiltersApplied || !canExport;
  const exportTooltip = !canExport
    ? exportDisabledMessage
    : !isFiltersApplied
      ? "Apply filters to enable export"
      : "";

  const isSaveAsSegmentDisabled = !hasSegmentFilters;
  const saveAsSegmentTooltip = !hasSegmentFilters
    ? "Configure the project filters to enable exporting as segment"
    : "";

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignSelf: "center", mr: 1 }}
        data-testid="workspace-export-actions"
      >
        {isCompanyListTab && (
          <Tooltip title={exportTooltip} placement="bottom">
            <span>
              <Button
                onClick={() => setIsExportModalOpen(true)}
                disabled={isExportDisabled}
                variant="text"
                color="secondary"
                size="small"
                startIcon={<DownloadIcon />}
                data-testid="workspace-export-btn"
              >
                Export
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title={saveAsSegmentTooltip} placement="bottom">
          <span>
            <Button
              onClick={() => setIsSegmentDialogOpen(true)}
              disabled={isSaveAsSegmentDisabled}
              variant="text"
              color="secondary"
              size="small"
              startIcon={<FontAwesomeIcon icon={faRectangleList} />}
              data-testid="workspace-save-as-segment-btn"
            >
              Save as Segment
            </Button>
          </span>
        </Tooltip>
      </Stack>
      <SegmentDialog
        open={isSegmentDialogOpen}
        mode="create"
        title="Save as Segment"
        initialName={defaultSegmentName}
        filtersJson={segmentFiltersJson}
        onClose={() => setIsSegmentDialogOpen(false)}
        onSave={() => setIsSegmentDialogOpen(false)}
      />
      {isExportModalOpen && (
        <ExportModal onClose={() => setIsExportModalOpen(false)} />
      )}
    </>
  );
};
